import { faLock } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import { faClose } from '@awesome.me/kit-3f20e419a7/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, ListGroup, Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { t } from 'react-switch-lang';
import Image from 'next/image';
import { useRouter } from 'next/router';
import styles from '../../styles/PaymentInformation.module.scss';
import { usePaymentProcessContext } from '../../context/PaymentProcessContext';
import { getLanguageString, getText } from '../../utils/Helpers';
import Button from '../Button';
import { events, logAmpEvent } from '../../utils/Amplitude';
import { FormatAmount } from '../../utils/Validations';
import { STEP } from '../../utils/Constants';

export function PaymentInformationMobileModal() {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    function handleMessage(event) {
      // subscribe to message events from the same origin
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'openPaymentInfoModal') {
        setShowModal(true);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <Modal
      show={showModal}
      onHide={() => {
        logAmpEvent(events.USER_CLOSED_TRANSACTION_DETAILS_MOBILE);
        setShowModal(false);
      }}
    >
      <Modal.Body className={styles.modalBody}>
        <Button
          fill="outline"
          onClick={() => setShowModal(false)}
          className={styles.modalCloseButton}
          ampEvent={events.USER_CLOSED_TRANSACTION_DETAILS_MOBILE}
          ariaLabel={t('PaymentInfo_Close_ButtonDescription')}
        >
          <FontAwesomeIcon icon={faClose} size="lg" />
        </Button>
        <PaymentInformation />
      </Modal.Body>
    </Modal>
  );
}

/**
 * @param {object} props
 * @param {import('../../context/PaymentProcessContext').LandingPageData} props.landingPageData
 * @param {import('../../context/PaymentProcessContext').PartnerInfo} props.partner
 */
export function RecipientLogo({ landingPageData, partner }) {
  const src = partner ? partner.LogoURL : landingPageData?.LogoUrl;
  const alt = partner ? getText(partner.LogoAlt) :
    getLanguageString(landingPageData?.LogoAltEn, landingPageData?.LogoAltFr);

  if (!src) return null;

  return (
    <div className={styles.partnerLogoContainer}>
      <Image
        src={src}
        alt={alt}
        style={{ objectFit: 'contain' }}
        fill
        sizes="(max-width: 768px) 100vw, 50vw"
        height={0}
        width={0}
        unoptimized // allow any remote urls
      />
    </div>
  );
}

// Desired order of the fields in account details section
const fieldOrder = [
  'Email',
  'FullName',
  'ClientRef',
  'PARTNERFIELD01',
  'PARTNERFIELD02',
  'PARTNERFIELD03',
  'PARTNERFIELD04',
  'PARTNERFIELD05',
  'PaymentAmount',
];

function PaymentInformation({
  className,
  hideSecureText = false,
  showEditButtons = false,
}) {
  const {
    paymentProcess: {
      step,
      landingPageData,
      accountDetails,
      isInvoice,
      paymentOption,
      partner,
      algoliaObject,
    },
  } = usePaymentProcessContext();
  const sortedAccountDetails = Object.entries(accountDetails || {})
    .sort(([keyA], [keyB]) => fieldOrder.indexOf(keyA) - fieldOrder.indexOf(keyB));

  const router = useRouter();
  const { landingPageKey } = router.query;
  const landingPageName =
    getLanguageString(landingPageData?.LandingPageNameEn, landingPageData?.LandingPageNameFr) ||
    getLanguageString(algoliaObject?.LandingPageNameEn, algoliaObject?.LandingPageNameFr);
  const recipientName =
    getText(partner?.DisplayName) ||
    getLanguageString(algoliaObject?.DisplayNameEn, algoliaObject?.DisplayNameFr);

  return (
    <Card className={`text-center ${className}`}>
      <Card.Header className={`${styles.paymentInfoTitleContainer} py-3`}>
        <h2>{t('PaymentInfo_Title')}</h2>
      </Card.Header>
      <ListGroup variant="flush" className={styles.paymentRecipientContainer} as="ul">
        <ListGroup.Item disabled as="li">
          <h3>{t('PaymentInfo_RecipientTitle')}</h3>
          <RecipientLogo landingPageData={landingPageData} partner={partner} />
          <p>{landingPageName}</p>
          <p className={styles.recipientName}>
            {recipientName || (step === STEP.CHOOSE_PARTNER && !landingPageKey && t('PaymentInfo_SelectRecipient'))}
          </p>
        </ListGroup.Item>
        {(step > STEP.DETAILS && accountDetails) && (
          <ListGroup.Item as="li" disabled={!showEditButtons}>
            <div className={styles.headingContainer}>
              {showEditButtons && !isInvoice && (
                <Button
                  id="editAccountDetails"
                  text={t('ReviewPage_Edit_ButtonText')}
                  fill="outline"
                  ampEvent={events.USER_CLICKED_REVIEWPAGE_EDIT_ACCOUNT_DETAILS}
                  onClick={() => router.push(`${window.location.pathname}`, undefined, { shallow: true })}
                  ariaLabel={t('ReviewPage_EditAccountDetails_ButtonDescription')}
                />
              )}
              <h3>{t('PaymentInfo_AccountDetailsTitle')}</h3>
            </div>
            {sortedAccountDetails.map(([key, value]) => {
              /** @type {import('../../context/PaymentProcessContext').Field} */
              let field;
              switch (key) {
                case 'Email':
                  field = {
                    Label: {
                      En: t('PaymentProcess_AccountDetails_EmailLabel', null, 'en'),
                      Fr: t('PaymentProcess_AccountDetails_EmailLabel', null, 'fr'),
                    },
                  };
                  break;
                case 'FullName':
                  field = {
                    Label: partner.PSWebConfig.AccountNameLabel,
                  };
                  break;
                case 'PaymentAmount':
                  field = {
                    Label: {
                      En: t('PaymentAmountField_Name', null, 'en'),
                      Fr: t('PaymentAmountField_Name', null, 'fr'),
                    },
                  };
                  break;
                default:
                  field = partner.PSWebConfig.Fields.find((f) => f.DBField === key);
              }
              if (!field || field.Type === 'Hidden') return null;
              return (
                <div key={key} className={styles.detailsItem}>
                  <p className={styles.detailsLabel}>
                    {getText(field.Label)}
                  </p>
                  <p>{key === 'PaymentAmount' ? FormatAmount(value) : value}</p>
                </div>
              );
            })}
          </ListGroup.Item>
        )}
        {(step > STEP.PAYMENT_OPTION && paymentOption) && (
          <ListGroup.Item as="li" disabled={!showEditButtons}>
            <div className={styles.headingContainer}>
              {showEditButtons && (
                <Button
                  id="editPaymentOption"
                  text={t('ReviewPage_Edit_ButtonText')}
                  fill="outline"
                  ampEvent={events.USER_CLICKED_REVIEWPAGE_EDIT_PAYMENT_OPTION}
                  onClick={() => router.push(`${window.location.pathname}#payment-option`, undefined, { shallow: true })}
                  ariaLabel={t('ReviewPage_EditPaymentOption_ButtonDescription')}
                />
              )}
              <h3>{t('PaymentInfo_PaymentOptionTitle')}</h3>
            </div>
            <p className={styles.paymentOptionTitle}>{getText(paymentOption.Title)}</p>
            <div className="my-1">
              <p className={styles.detailsLabel}>{t('PaymentInfo_ProcessTimeLabel')}</p>
              <p>{getText(paymentOption.Delivery)}</p>
            </div>
            <div>
              <p className={styles.detailsLabel}>{t('PaymentInfo_ProcessingFeeLabel')}</p>
              <p>{FormatAmount(paymentOption.totalFee)}</p>
            </div>
          </ListGroup.Item>
        )}
      </ListGroup>
      {(step > STEP.PAYMENT_OPTION && paymentOption) && (
        <Card.Footer className={`${styles.cardFooter} py-3 `}>
          <h3>
            {t('PaymentInformation_Payment_Total').replace('xPaymentTotal', FormatAmount(paymentOption.totalAmount))}
          </h3>
        </Card.Footer>
      )}
      {!hideSecureText && (
        <Card.Footer className={`${styles.cardFooter} py-3 `}>
          <FontAwesomeIcon
            icon={faLock}
            className={`me-2 ${styles.greenFont}`}
          />
          <p className={styles.greenFont}>
            {t('PaymentInfo_SecureSiteText')}
          </p>
        </Card.Footer>
      )}
    </Card>

  );
}

export default function PaymentInformationWithModal(props) {
  return (
    <>
      <PaymentInformation {...props} />
      <PaymentInformationMobileModal />
    </>
  );
}
